import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  ListArInvoicesInput,
  ListArInvoicesResponse,
  GetArInvoiceResponse,
  ArInvoiceListItem,
} from '@erp/rpc';

export function useArInvoicesList(
  filters: ListArInvoicesInput = {},
  options: {
    /**
     * Keep showing the previous result set while a new one loads — for
     * search-as-you-type consumers (comboboxes) so the list doesn't flash
     * empty between keystrokes.
     */
    keepPreviousData?: boolean;
  } = {},
) {
  const query = useQuery({
    ...orpc.accountsReceivable.arInvoices.list.queryOptions({ input: filters }),
    ...(options.keepPreviousData ? { placeholderData: keepPreviousData } : {}),
  });

  const invoices = query.data?.invoices ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    invoices,
    pagination,
  } satisfies typeof query & {
    data?: ListArInvoicesResponse;
    invoices: ArInvoiceListItem[];
    pagination: ListArInvoicesResponse['pagination'] | undefined;
  };
}

export function useArInvoice(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...orpc.accountsReceivable.arInvoices.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    invoice: query.data?.invoice ?? null,
  } satisfies typeof query & {
    data?: GetArInvoiceResponse;
    invoice: GetArInvoiceResponse['invoice'] | null;
  };
}
